import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, ScanBarcode, XCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm, organisation, branches } = useAuth();
  const [bill, setBill] = useState(null);
  const [resending, setResending] = useState(false);
  const reload = () => api.get(`/sales-bills/${id}`).then((r) => setBill(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const cancel = async () => {
    if (!window.confirm("Cancel this bill? Stock will be restored.")) return;
    try { await api.post(`/sales-bills/${id}/cancel`); toast.success("Cancelled"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resend = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/sales-bills/${id}/resend-whatsapp`);
      toast.success(`Resent via ${data.via}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  if (!bill) return <div className="text-slate-500">Loading...</div>;
  const canCancel = !bill.cancelled && !bill.on_hold && (user.role !== "cashier" || hasPerm("bill_cancel"));
  const canResend = bill.customer_phone && !bill.cancelled && (user.role !== "cashier" || hasPerm("whatsapp_bill_toggle"));
  // FIX 4: read organisation & branch names from context so bill header is dynamic
  const orgName = organisation?.name || "";
  const branchName = branches?.find((b) => b.id === bill.branch_id)?.name || "";
  const orgSettings = organisation?.settings || {};
  const brandName = orgSettings.bill_brand_name || orgName;
  const billPhone = orgSettings.bill_contact_phone || "";
  const showPhone = orgSettings.bill_show_phone !== false && !!billPhone;
  const showDescription = orgSettings.bill_show_description !== false;
  const hideGst = !!bill.hide_gst_on_customer_bill;
  const anyDescription = (bill.items || []).some((it) => (it.description || "").trim());

  return (
    <div className="space-y-6" data-testid="bill-detail-page">
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-none"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
        <div className="flex gap-2">
          {canResend && <Button onClick={resend} disabled={resending} variant="outline" className="rounded-none border-green-300 text-green-700 hover:bg-green-50" data-testid="bill-resend-wa-btn"><MessageSquare className="w-4 h-4 mr-2" />{resending ? "Sending…" : "Resend WhatsApp"}</Button>}
          {canCancel && <Button onClick={cancel} variant="outline" className="rounded-none border-red-300 text-red-600 hover:bg-red-50" data-testid="bill-cancel-btn"><XCircle className="w-4 h-4 mr-2" />Cancel Bill</Button>}
          <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="bill-print-btn"><Printer className="w-4 h-4 mr-2" />Print</Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-8 md:p-12 max-w-3xl mx-auto print:border-0 print:shadow-none print:p-0" id="invoice">
        {/* FIX 4: Organisation name + branch name at top. "eaassy" wordmark removed. */}
        <div className="flex justify-between items-start border-b-2 border-slate-900 pb-6 mb-6">
          <div>
            <div className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }} data-testid="bill-org-name">{brandName}</div>
            {branchName && <div className="text-sm font-semibold text-slate-700 mt-0.5" data-testid="bill-branch-name">{branchName}</div>}
            {showPhone && <div className="text-xs text-slate-600 mt-0.5">📞 {billPhone}</div>}
            <div className="text-xs text-slate-500 mt-2 uppercase tracking-[0.2em]">Tax Invoice</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Bill Number</div>
            <div className="font-mono font-bold">{bill.bill_number}</div>
            <div className="text-xs text-slate-500 mt-2">{new Date(bill.created_at).toLocaleString("en-IN")}</div>
            {bill.cancelled && <div className="mt-2 inline-block border border-red-500 text-red-600 px-2 py-1 text-xs font-bold uppercase tracking-widest">CANCELLED</div>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
          <div><div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Billed To</div><div className="font-semibold">{bill.customer_name}</div>{bill.customer_phone && <div className="text-slate-600">{bill.customer_phone}</div>}</div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Cashier</div>
            <div className="font-semibold">{bill.cashier_name}</div>
            {bill.salesman_name && <div className="text-xs text-slate-600 mt-1"><span className="uppercase tracking-widest text-slate-400">Salesman: </span>{bill.salesman_name}</div>}
            <div className="text-slate-600 mt-0.5">{bill.payment_method}</div>
          </div>
        </div>
        <table className="w-full text-sm border-t border-b border-slate-200 mb-6">
          <thead className="bg-slate-50"><tr>
            <th className="px-3 py-2 text-left font-semibold">Item</th>
            {showDescription && anyDescription && <th className="px-3 py-2 text-left font-semibold">Description</th>}
            <th className="px-3 py-2 text-right font-semibold">Qty</th>
            <th className="px-3 py-2 text-right font-semibold">MRP</th>
            <th className="px-3 py-2 text-right font-semibold">Rate</th>
            {!hideGst && <th className="px-3 py-2 text-right font-semibold">GST</th>}
            <th className="px-3 py-2 text-right font-semibold">Amount</th>
          </tr></thead>
          <tbody>{bill.items.map((it, i) => {
            const mrp = parseFloat(it.mrp || 0);
            const fd = parseFloat(it.fake_discount_percent || 0);
            const effRate = it.line_total / Math.max(it.quantity, 1);
            const hasDiscount = mrp > 0 && mrp > effRate + 0.01;
            return (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-2"><div className="font-medium">{it.name}</div><div className="text-xs text-slate-500 font-mono">{it.barcode}</div></td>
                {showDescription && anyDescription && <td className="px-3 py-2 text-xs text-slate-600 max-w-[200px]">{it.description || "-"}</td>}
                <td className="px-3 py-2 text-right tabular-nums">{it.quantity}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {mrp > 0 ? (
                    <div className="flex flex-col items-end">
                      <span className={hasDiscount ? "line-through text-slate-400 text-xs" : "tabular-nums"}>{formatINR(mrp)}</span>
                      {hasDiscount && fd > 0 && <span className="text-[10px] text-green-700 font-semibold mt-0.5">{fd.toFixed(0)}% OFF</span>}
                    </div>
                  ) : "-"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{formatINR(effRate)}</td>
                {!hideGst && <td className="px-3 py-2 text-right tabular-nums">{it.gst_percent}%</td>}
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatINR(it.line_total)}</td>
              </tr>
            );
          })}</tbody>
        </table>
        <div className="flex justify-end">
          <div className="w-full md:w-72 space-y-1 text-sm">
            {!hideGst && <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="tabular-nums">{formatINR(bill.subtotal)}</span></div>}
            {!hideGst && <div className="flex justify-between"><span className="text-slate-600">CGST</span><span className="tabular-nums">{formatINR(bill.cgst_total)}</span></div>}
            {!hideGst && <div className="flex justify-between"><span className="text-slate-600">SGST</span><span className="tabular-nums">{formatINR(bill.sgst_total)}</span></div>}
            {/* MRP / fake-discount summary — always show when there are savings */}
            {(bill.total_mrp || 0) > 0 && (bill.total_savings || 0) > 0 && (
              <>
                <div className="flex justify-between"><span className="text-slate-600">Total MRP</span><span className="tabular-nums line-through text-slate-400">{formatINR(bill.total_mrp)}</span></div>
                <div className="flex justify-between text-green-700 font-semibold"><span>You Saved</span><span className="tabular-nums">- {formatINR(bill.total_savings)}</span></div>
              </>
            )}
            {bill.discount > 0 && <div className="flex justify-between"><span className="text-slate-600">Discount</span><span className="tabular-nums">- {formatINR(bill.discount)}</span></div>}
            {bill.loyalty_redemption_value > 0 && <div className="flex justify-between"><span className="text-slate-600">Loyalty redeemed</span><span className="tabular-nums">- {formatINR(bill.loyalty_redemption_value)}</span></div>}
            {(bill.round_off || 0) !== 0 && (
              <div className="flex justify-between"><span className="text-slate-600">Round-off</span><span className="tabular-nums">{bill.round_off > 0 ? "+" : ""}{formatINR(bill.round_off)}</span></div>
            )}
            <div className="flex justify-between border-t-2 border-slate-900 pt-2 mt-2"><span className="font-bold">Grand Total</span><span className="font-black tabular-nums" data-testid="invoice-grand-total">{formatINR(bill.grand_total)}</span></div>
            {bill.loyalty_earned > 0 && <div className="text-xs text-green-700 text-right pt-2">+{bill.loyalty_earned} loyalty points earned</div>}
          </div>
        </div>
        <div className="mt-12 text-center text-xs text-slate-500 border-t border-slate-200 pt-4">Thank you for your business</div>
      </div>
      <style>{`@media print { body { background: white; } .print\\:hidden { display: none !important; } aside, header { display: none !important; } main { padding: 0 !important; } #invoice { box-shadow: none; border: none; } }`}</style>
    </div>
  );
}
